import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { NamedLink, CategoryGrid, BrandGrid, Reveal, ListingCard } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { getHighlightedListings } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { useIntl } from '../../util/reactIntl';
import { RegNumInput } from '../WP/RegNumInput';

import heroBg from '../../assets/hero-bg.jpg';
import heroKopa from '../../assets/hero-kopa.jpg';
import heroSalja from '../../assets/hero-salja.jpg';
import lagginHero from '../../assets/laggin.jpg';
import saljsmidigtHero from '../../assets/saljsmidigt.jpg';
import sokHero from '../../assets/sok.jpg';
import valjochjamforHero from '../../assets/valjochjamfor.jpg';
import kontaktaHero from '../../assets/kontakta.jpg';
import faforfragningarHero from '../../assets/faforfragningar.jpg';
import regnummerHero from '../../assets/regnummer-hero.jpg';
import sustainabilityBg from '../../assets/sustainability-bg.jpg';
import videoThumb from '../../assets/video-thumb.jpg';
import trustpilotLogoLight from '../../assets/trustpilot-logo.svg';
import trustpilotLogoDark from '../../assets/trustpilot-logo-dark.svg';
import trustpilotStars from '../../assets/trustpilot-stars.svg';

import css from './WheelplaceLanding.module.css';
import WheelplaceLandingMobile from './WheelplaceLandingMobile';

const HERO_IMAGES = {
  default: heroBg,
  kopa: heroKopa,
  salja: heroSalja,
};

const Hero = () => {
  const [activeBg, setActiveBg] = useState('default');
  const [heroRegNum, setHeroRegNum] = useState('');

  return (
    <section className={css.hero}>
      <div className={css.heroBgLayer}>
        {Object.entries(HERO_IMAGES).map(([key, src]) => (
          <img
            key={key}
            src={src}
            alt=""
            className={classNames(css.heroBg, { [css.heroBgActive]: activeBg === key })}
          />
        ))}
        <div className={css.heroOverlay} />
      </div>

      <div className={css.heroInner}>
        <h1 className={css.heroTitle}>
          Köp och sälj begagnade eller
          <br />
          nya hjul, däck och fälgar
        </h1>
        <p className={css.heroSubtitle}>
          Sveriges smartaste marknadsplats för hjul. Sök med registreringsnummer — vi hjälper dig
          hitta rätt.
        </p>

        {/* Mobile-only plate search — primary thumb action lives above the fold
         * on mobile. Köpa/Sälja cards below take over on larger screens. */}
        <div className={css.heroMobilePlate}>
          <RegNumInput
            page="searchPage"
            listingType="wheel"
            savedRegNum={heroRegNum}
            changeRegNum={setHeroRegNum}
          />
          <div className={css.heroMobileShortcuts}>
            <NamedLink name="SearchPage" className={css.heroMobileShortcut}>
              Bläddra alla annonser
            </NamedLink>
            <span className={css.heroMobileShortcutDivider} aria-hidden="true">·</span>
            <NamedLink name="WheelplaceCreateListingPage" className={css.heroMobileShortcut}>
              Lägg in annons
            </NamedLink>
          </div>
        </div>

        <div className={css.heroCards}>
          <NamedLink
            name="SearchPage"
            className={css.heroCard}
            onMouseEnter={() => setActiveBg('kopa')}
            onMouseLeave={() => setActiveBg('default')}
          >
            <span className={css.heroCardIcon}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                />
              </svg>
            </span>
            <h2 className={css.heroCardTitle}>Köpa</h2>
            <p className={css.heroCardDesc}>
              Hitta hjul som passar din bil. Sök med regnummer eller bläddra bland annonser.
            </p>
            <span className={css.heroCardCta}>
              Sök annonser
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </NamedLink>

          <NamedLink
            name="WheelplaceCreateListingPage"
            className={css.heroCard}
            onMouseEnter={() => setActiveBg('salja')}
            onMouseLeave={() => setActiveBg('default')}
          >
            <span className={css.heroCardIcon}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
            </span>
            <h2 className={css.heroCardTitle}>Sälja</h2>
            <p className={css.heroCardDesc}>
              Nå tusentals köpare. Skapa en annons på under 2 minuter — helt gratis.
            </p>
            <span className={css.heroCardCta}>
              Lägg in annons
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </NamedLink>
        </div>

        <a
          href="https://www.trustpilot.com/review/www.wheelplace.com"
          target="_blank"
          rel="noopener noreferrer"
          className={css.trustpilotHero}
          aria-label="Läs omdömen om Wheelplace på Trustpilot"
        >
          <img src={trustpilotLogoLight} alt="Trustpilot" />
          <img src={trustpilotStars} alt="4.3 av 5" className={css.trustpilotHeroStars} />
          <p className={css.trustpilotHeroCopy}>
            <strong>TrustScore 4.3</strong> · 39 omdömen
          </p>
        </a>
      </div>
    </section>
  );
};

const pickImageUrl = image => {
  const v = image?.attributes?.variants || {};
  return (
    v['listing-card-2x']?.url ||
    v['listing-card']?.url ||
    v['scaled-medium']?.url ||
    v['square-small2x']?.url ||
    v['square-small']?.url ||
    null
  );
};

const getSpecs = publicData => {
  if (!publicData) return null;
  const { tireWidthFront, profileFront, diameter, threaddepth, condition } = publicData;
  const parts = [];
  if (tireWidthFront && profileFront && diameter) {
    parts.push(`${tireWidthFront}/${profileFront}R${diameter}`);
  }
  if (threaddepth) parts.push(threaddepth);
  if (condition) parts.push(condition);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const FeaturedListingCard = ({ listing, intl }) => {
  const { title, price, publicData } = listing.attributes || {};
  const imageUrl = pickImageUrl(listing.images?.[0]);
  const listingLink = listingPageLinkProps(listing);

  const authorProfile = listing.author?.attributes?.profile;
  const authorName = authorProfile?.displayName || '';
  const isBusiness = authorProfile?.publicData?.userType === 'business';
  const city = publicData?.city;
  const isNew = publicData?.condition === 'Nya';
  const specs = getSpecs(publicData);

  const formattedPrice = price ? formatMoney(intl, price) : null;

  return (
    <NamedLink {...listingLink} className={css.featuredCard}>
      <div className={css.featuredImageWrap}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className={css.featuredImage} loading="lazy" />
        ) : (
          <div className={css.featuredImagePlaceholder} />
        )}
        {isNew && <span className={css.featuredNewBadge}>Nya</span>}
      </div>

      <div className={css.featuredBody}>
        <h3 className={css.featuredTitle}>{title}</h3>
        {specs && <p className={css.featuredSpecs}>{specs}</p>}

        <div className={css.featuredFooter}>
          <div className={css.featuredSeller}>
            <span className={css.featuredSellerName}>{authorName}</span>
            {isBusiness && (
              <svg className={css.featuredVerified} viewBox="0 0 20 20" fill="currentColor" aria-label="Verifierad">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          {city && (
            <div className={css.featuredLocation}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              <span>{city}</span>
            </div>
          )}
        </div>

        {formattedPrice && <p className={css.featuredPrice}>{formattedPrice}</p>}
      </div>
    </NamedLink>
  );
};

const FeaturedListings = () => {
  const config = useConfiguration();
  const intl = useIntl();
  const scrollRef = useRef(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { Money } = sdkTypes;

  useEffect(() => {
    const listingTypes = config.listing?.listingTypes
      ?.filter(l => l.listingType !== 'admin-adverts' && l.listingType !== 'buying')
      .map(l => l.listingType)
      .join(',');

    let cancelled = false;
    getHighlightedListings({ listingTypes })
      .then(response => {
        if (cancelled) return;
        const included = response?.data?.included || [];
        const byId = id => included.find(x => x.id.uuid === id);
        const hydrated = (response?.data?.data || []).map(listing => {
          if (listing?.attributes?.price) {
            listing.attributes.price = new Money(
              listing.attributes.price.amount,
              listing.attributes.price.currency
            );
          }
          const { relationships, ...rest } = listing;
          const images = (relationships?.images?.data || [])
            .map(img => byId(img.id.uuid))
            .filter(Boolean);
          const author = byId(relationships?.author?.data?.id?.uuid);
          return { ...rest, images, author };
        });
        setListings(hydrated);
      })
      .catch(err => {
        console.error('Failed to load highlighted listings', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scroll = dir => {
    if (!scrollRef.current) return;
    const card = scrollRef.current.querySelector('[data-card]');
    const step = (card?.offsetWidth || 300) + 20;
    scrollRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (!loading && listings.length === 0) return null;

  return (
    <section className={css.section}>
      <div className={css.container}>
        <div className={css.sectionHead}>
          <div className={css.sectionHeadLeft}>
            <span className={css.eyebrow}>Marknadsplats</span>
            <h2>Senaste annonserna</h2>
            <p>Nyss inlagda hjul, däck och fälgar.</p>
          </div>
          <div className={css.scrollBtns}>
            <button
              type="button"
              onClick={() => scroll(-1)}
              className={css.scrollBtn}
              aria-label="Föregående"
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              className={css.scrollBtn}
              aria-label="Nästa"
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Use the canonical <ListingCard /> so the landing carousel
          * matches /annonser, ManageFavourites, SellerProfile — one
          * card shape for all listing surfaces. FeaturedListingCard
          * was a parallel implementation with its own hierarchy that
          * fell out of sync with the spec-first rebuild. */}
        <div ref={scrollRef} className={css.listingsScroller}>
          {listings.map(listing => (
            <div key={listing.id.uuid} data-card className={css.listingCardCompact}>
              <ListingCard listing={listing} showFavoriteIcons />
            </div>
          ))}
        </div>

        <div className={css.viewAllWrap}>
          <NamedLink name="SearchPage" className={css.viewAll}>
            Visa alla annonser
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
 *  HowItWorks — editorial 3-step walkthrough with buyer/seller
 *  tab toggle. Replaces the old three-card intent router
 *  ("Tre sätt att hitta rätt hjul"), which duplicated the hero's
 *  routing job without teaching anything. This section earns its
 *  real estate by being the only place on the page that walks
 *  the user through the full transaction loop.
 * ============================================================ */

const HOW_IT_WORKS_FLOWS = {
  buyer: {
    tabLabel: 'För köpare',
    steps: [
      {
        id: 'buyer-01',
        image: sokHero,
        alt: 'Regnummersökning i mobilen framför bilen på uppfarten',
        title: 'Sök med regnummer',
        desc: 'Skriv ditt regnr och se direkt vilka hjul som passar din bil.',
      },
      {
        id: 'buyer-02',
        image: valjochjamforHero,
        alt: 'Bläddrar bland annonser på Wheelplace i laptopen',
        title: 'Välj och jämför',
        desc: 'Bläddra bland matchande annonser och spara dina favoriter.',
      },
      {
        id: 'buyer-03',
        image: kontaktaHero,
        alt: 'Köpare inspekterar ett däck i bilen',
        title: 'Kontakta säljaren',
        desc: 'Kom överens om pris, upphämtning eller frakt direkt med säljaren.',
      },
    ],
    cta: { label: 'Sök med regnummer', route: 'SearchPage' },
  },
  seller: {
    tabLabel: 'För säljare',
    steps: [
      {
        id: 'seller-01',
        image: lagginHero,
        alt: 'Fotograferar ett däck med mobilen inför annonsering',
        title: 'Lägg in annons',
        desc: 'Registrera annonsen på under två minuter — helt utan avgift.',
      },
      {
        id: 'seller-02',
        image: faforfragningarHero,
        alt: 'Säljare läser förfrågningar i mobilen i däckhallen',
        title: 'Få förfrågningar',
        desc: 'Köpare i hela Sverige ser din annons och hör av sig direkt.',
      },
      {
        id: 'seller-03',
        image: saljsmidigtHero,
        alt: 'Säljare och köpare lastar in däcket i bakluckan',
        title: 'Sälj smidigt',
        desc: 'Kom överens med köparen. Ingen kommission, ingen mellanhand.',
      },
    ],
    cta: { label: 'Lägg in annons', route: 'WheelplaceCreateListingPage' },
  },
};

const ArrowRightIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

export const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState('buyer');
  const tabsRef = useRef(null);
  const flow = HOW_IT_WORKS_FLOWS[activeTab];

  /* Arrow-key tab switching per ARIA tab pattern. */
  const handleTabKeyDown = e => {
    const order = ['buyer', 'seller'];
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const next = order[(order.indexOf(activeTab) + dir + order.length) % order.length];
      setActiveTab(next);
      const nextBtn = tabsRef.current?.querySelector(`[data-tab-id="${next}"]`);
      nextBtn?.focus();
    }
  };

  return (
    <section className={classNames(css.section, css.howItWorksSection)}>
      <div className={css.container}>
        <div className={css.howHeader}>
          <span className={css.howEyebrow}>Så funkar det</span>
          <h2 className={css.howTitle}>Enklare än du tror.</h2>
        </div>

        <div
          ref={tabsRef}
          className={css.howTabsRow}
          role="tablist"
          aria-label="Visa flödet för köpare eller säljare"
          onKeyDown={handleTabKeyDown}
        >
          {Object.entries(HOW_IT_WORKS_FLOWS).map(([id, cfg]) => {
            const isActive = id === activeTab;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`howtab-${id}`}
                data-tab-id={id}
                aria-selected={isActive}
                aria-controls={`howpanel-${id}`}
                tabIndex={isActive ? 0 : -1}
                className={classNames(css.howTab, { [css.howTabActive]: isActive })}
                onClick={() => setActiveTab(id)}
              >
                {cfg.tabLabel}
              </button>
            );
          })}
        </div>

        <div
          key={activeTab}
          id={`howpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`howtab-${activeTab}`}
          className={css.howPanel}
        >
          <ol className={css.howStepsGrid}>
            {flow.steps.map((step, i) => (
              <li key={step.id} className={css.howStep}>
                <div className={css.howStepMedia}>
                  <img src={step.image} alt={step.alt} loading="lazy" />
                </div>
                <span className={css.howStepNumber} aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className={css.howStepTitle}>{step.title}</h3>
                <p className={css.howStepDesc}>{step.desc}</p>
              </li>
            ))}
          </ol>

          <div className={css.howCtaWrap}>
            <NamedLink name={flow.cta.route} className={css.howCtaButton}>
              {flow.cta.label}
              <ArrowRightIcon />
            </NamedLink>
            <p className={css.howCtaCopy}>Helt gratis — inga avgifter för privatpersoner.</p>
          </div>
        </div>

        <p className={css.howFallbackLink}>
          Hittar du inte det du söker?{' '}
          <NamedLink name="SavedSearchesPage" className={css.howFallbackCta}>
            Bevaka din sökning
            <ArrowRightIcon />
          </NamedLink>
        </p>
      </div>
    </section>
  );
};

const CategoriesSection = () => (
  <section className={classNames(css.section, css.sectionGray)}>
    <div className={css.container}>
      <span className={classNames(css.eyebrow, css.eyebrowCentered)}>Kategorier</span>
      <h2 className={css.sectionTitle}>Hitta efter kategori</h2>
      <p className={css.sectionLead}>Bläddra bland de populäraste kategorierna.</p>
    </div>
    <CategoryGrid />
  </section>
);

const PlateBand = () => {
  const [regNum, setRegNum] = useState('');
  return (
    <section className={css.plateBand}>
      <div className={css.plateBandBg}>
        <img src={regnummerHero} alt="" />
        <div className={css.plateBandOverlay} />
      </div>
      <div className={css.plateBandInner}>
        <div>
          <h2 className={css.plateBandTitle}>Sök med registreringsnummer</h2>
          <p className={css.plateBandCopy}>
            Skriv in ditt regnummer så visar vi vilka hjul, däck och fälgar som passar just din bil.
          </p>
        </div>
        <div className={css.plateBandInput}>
          <RegNumInput
            page="searchPage"
            listingType="wheel"
            savedRegNum={regNum}
            changeRegNum={setRegNum}
          />
        </div>
      </div>
    </section>
  );
};

const BrandsSection = () => (
  <section className={css.section}>
    <div className={css.container}>
      <span className={classNames(css.eyebrow, css.eyebrowCentered)}>Bilmärken</span>
      <h2 className={css.sectionTitle}>Sök efter bilmärke</h2>
      <p className={css.sectionLead}>Hitta hjul som passar just din bil.</p>
    </div>
    <BrandGrid />
  </section>
);

const TESTIMONIALS = [
  {
    quote:
      'Hittade exakt rätt vinterdäck till min Volvo på bara några minuter. Registreringsnummersökningen är genial!',
    author: 'Anna K.',
    rating: 5,
  },
  {
    quote:
      'Sålde mina gamla sommarhjul samma vecka. Enkel process och bra kundsupport.',
    author: 'Marcus L.',
    rating: 5,
  },
  {
    quote:
      'Äntligen en seriös marknadsplats bara för hjul. Slipper bläddra igenom tusentals irrelevanta annonser.',
    author: 'Erik S.',
    rating: 4,
  },
];

const Stars = ({ count }) => (
  <span className={css.testimonialStars}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        viewBox="0 0 20 20"
        className={i >= count ? css.testimonialStarMuted : undefined}
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.96a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.287 3.96c.3.922-.755 1.688-1.54 1.118l-3.367-2.446a1 1 0 00-1.176 0l-3.366 2.446c-.785.57-1.84-.196-1.54-1.118l1.286-3.96a1 1 0 00-.364-1.118L2.06 9.387c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.289-3.96z" />
      </svg>
    ))}
  </span>
);

const SocialProof = () => (
  <section className={css.section}>
    <div className={css.container}>
      <span className={classNames(css.eyebrow, css.eyebrowCentered)}>Omdömen</span>
      <h2 className={css.sectionTitle}>Vad våra användare säger</h2>
      <p className={css.sectionLead}>
        Köpare och säljare som redan hittat rätt på Wheelplace.
      </p>

      <div className={css.testimonialsGrid}>
        {TESTIMONIALS.map(t => (
          <div key={t.author} className={css.testimonial}>
            <Stars count={t.rating} />
            <p className={css.testimonialQuote}>{t.quote}</p>
            <p className={css.testimonialAuthor}>— {t.author}</p>
          </div>
        ))}
      </div>

      <a
        href="https://www.trustpilot.com/review/www.wheelplace.com"
        target="_blank"
        rel="noopener noreferrer"
        className={css.trustpilotFoot}
        aria-label="Läs omdömen om Wheelplace på Trustpilot"
      >
        <img src={trustpilotLogoDark} alt="Trustpilot" />
        <img src={trustpilotStars} alt="4.3 av 5" className={css.trustpilotHeroStars} />
        <p className={css.trustpilotFootCopy}>
          <strong>TrustScore 4.3</strong> · 39 omdömen
        </p>
      </a>
    </div>
  </section>
);

/* ================================================================
 * Hjulguiden — editorial brand moment on the landing page.
 *
 * Magazine-style header (eyebrow + statement H2 + lead), pill tabs
 * for category switch, and a 2-col numbered spec grid below. No
 * illustrations — the typography and spacing carry the brand
 * moment.
 * ================================================================ */

const GUIDE_TABS = [
  {
    id: 'dack',
    label: 'Däck',
    title: 'Allt du behöver veta om däck',
    lead: 'Storlek, mönsterdjup, ålder och hastighetsindex — det här är vad som avgör om ett däck passar dig.',
    content: [
      { label: 'Dimension', desc: 'Anges som t.ex. 205/55 R16 — bredd / profil / fälgstorlek i tum.' },
      { label: 'Mönsterdjup', desc: 'Nytt däck: ca 8 mm. Lagkrav: minst 1,6 mm. Rekommenderat vinter: 3 mm+.' },
      { label: 'Hastighetsindex', desc: 'Bokstaven efter storleken (t.ex. "V") anger däckets maxhastighet.' },
      { label: 'DOT-nummer', desc: 'Fyra siffror som visar tillverkningsvecka och år (2423 = vecka 24, 2023).' },
    ],
  },
  {
    id: 'falgar',
    label: 'Fälgar',
    title: 'Välj rätt fälgar',
    lead: 'Bultmönster, inpressning och centrumhål måste matcha din bil — annars passar fälgen inte.',
    content: [
      { label: 'Bultmönster', desc: 'T.ex. 5x108 — antal bultar × cirkelns diameter i mm.' },
      { label: 'ET-tal', desc: 'Inpressningen i mm. Avgör hur långt ut hjulet sticker i hjulhuset.' },
      { label: 'Centrumhål', desc: 'Navhålets diameter. Måste matcha navet eller använda centrumringar.' },
      { label: 'Material', desc: 'Aluminium: lättare och snyggare. Stål: billigare och tåligare.' },
    ],
  },
  {
    id: 'kompletta',
    label: 'Kompletta hjul',
    title: 'Kompletta hjul — redo att rulla',
    lead: 'Färdigmonterade däck på fälg. Sparar tid vid säsongsbyte och ofta pengar som paket.',
    content: [
      { label: 'Vad ingår?', desc: 'Däck monterade på fälg — klara att sättas på bilen direkt.' },
      { label: 'Fördelar', desc: 'Sparar tid och pengar vid säsongsbyte. Inga losspackade delar.' },
      { label: 'TPMS', desc: 'Nyare bilar kräver tryckventiler — kontrollera att de följer med.' },
      { label: 'Balansering', desc: 'Kompletta hjul bör vara balanserade. Dubbelkolla med säljaren.' },
    ],
  },
];

export const ProductEducation = () => {
  const [active, setActive] = useState('dack');
  const current = GUIDE_TABS.find(t => t.id === active);

  return (
    <section className={classNames(css.section, css.guideSection)}>
      <div className={css.container}>
        <div className={css.guideHeader}>
          <span className={css.guideEyebrow}>Hjulguiden</span>
          <h2 className={css.guideTitle}>Smarta val börjar med rätt kunskap.</h2>
          <p className={css.guideLead}>
            En snabbguide i vad du ska titta efter — välj kategori och läs det viktigaste på
            en halv minut.
          </p>
        </div>

        <div className={css.guideTabsRow} role="tablist" aria-label="Hjulguiden kategorier">
          {GUIDE_TABS.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active === t.id}
              onClick={() => setActive(t.id)}
              className={classNames(css.guideTab, { [css.guideTabActive]: active === t.id })}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Re-mount the panel on tab change so the fade-up animation
          * replays every time. key={active} triggers React to discard
          * the previous subtree and mount a fresh one. */}
        <div key={active} className={css.guidePanel}>
          <div className={css.guidePanelHeader}>
            <h3 className={css.guidePanelTitle}>{current.title}</h3>
            {current.lead ? <p className={css.guidePanelLead}>{current.lead}</p> : null}
          </div>

          <ol className={css.guideSpecGrid}>
            {current.content.map((item, i) => (
              <li key={item.label} className={css.guideSpec}>
                <span className={css.guideSpecNumber}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className={css.guideSpecBody}>
                  <h4 className={css.guideSpecLabel}>{item.label}</h4>
                  <p className={css.guideSpecDesc}>{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

const Sustainability = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section className={css.sustainability}>
      <img src={sustainabilityBg} alt="" className={css.sustainabilityBg} />
      <div className={css.sustainabilityOverlay} />
      <div className={css.sustainabilityInner}>
        <span className={classNames(css.eyebrow, css.eyebrowOnDark)}>Hållbarhet</span>
        <h2 className={css.sustainabilityTitle}>
          Varje däck förtjänar
          <br />
          en andra chans
        </h2>
        <p className={css.sustainabilityLead}>
          Vi tror på en framtid där varje däck och fälg får en ökad livslängd. Genom att ge hjul
          ett andra liv minskar vi avfall och sparar jordens resurser.
        </p>

        <div className={css.videoFrame}>
          {playing ? (
            <iframe
              src="https://www.youtube.com/embed/X6x1yPQ7n6A?autoplay=1"
              title="Wheelplace — Hållbarhet"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={css.videoIframe}
            />
          ) : (
            <button
              type="button"
              className={css.videoThumbButton}
              onClick={() => setPlaying(true)}
              aria-label="Spela video"
            >
              <img src={videoThumb} alt="" className={css.videoThumbImg} />
              <span className={css.videoThumbInner}>
                <span className={css.videoPlay}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            </button>
          )}
        </div>

        <div className={css.statsRow}>
          {[
            { title: '12 450+', desc: 'Hjul har fått nytt liv via Wheelplace' },
            { title: 'Minskat avfall', desc: 'Återanvända hjul innebär färre på deponi' },
            { title: 'Sparade resurser', desc: 'Tillverkning av nya däck kräver enorma resurser' },
            { title: 'Cirkulär ekonomi', desc: 'Vi kopplar samman säljare och köpare direkt' },
          ].map(s => (
            <div key={s.title} className={css.stat}>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const B2BSection = () => (
  <section className={classNames(css.section, css.b2b)}>
    <div className={css.container}>
      <div className={css.b2bGrid}>
        <div className={css.b2bIntro}>
          <span className={css.eyebrow}>För företag</span>
          <h2>Verkstad eller bilhandlare?</h2>
          <p>
            Nå tusentals köpare som aktivt letar efter hjul. Wheelplace ger dig en dedikerad kanal
            för att sälja lagersaldo, ta emot förfrågningar och bygga kundrelationer.
          </p>
          <ul className={css.b2bList}>
            {[
              'Eget företagskonto med obegränsade annonser',
              'Synlighet mot köpare som söker med regnummer',
              'Förfrågningar direkt från kunder i ditt område',
              'Oberoende kundsupport som stöttar köpprocessen',
            ].map(item => (
              <li key={item}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <NamedLink name="CMSPage" params={{ pageId: 'kontakt' }} className={css.b2bCta}>
            Kontakta oss
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </NamedLink>
        </div>

        <div className={css.b2bCard}>
          <div className={css.b2bCardHead}>
            <span className={css.b2bCardIcon}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
                />
              </svg>
            </span>
            <h3>Företagskonto</h3>
            <p>Nå fler kunder idag</p>
          </div>
          <div className={css.b2bRows}>
            {[
              { label: 'Aktiva annonser', value: 'Obegränsat' },
              { label: 'Förfrågningar', value: 'Direkt' },
              { label: 'Support', value: 'Prioriterad' },
              { label: 'Pris', value: 'Kontakta oss' },
            ].map(row => (
              <div key={row.label} className={css.b2bRow}>
                <span className={css.b2bRowLabel}>{row.label}</span>
                <span className={css.b2bRowValue}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FAQS = [
  {
    q: 'Hur fungerar det att köpa hjul på Wheelplace?',
    a: 'Sök bland annonser eller skriv in ditt regnummer. Kontakta säljaren direkt för frågor och betalning.',
  },
  {
    q: 'Hur lägger jag upp en annons?',
    a: 'Skapa ett konto, klicka på "Lägg in annons" och fyll i uppgifterna. Det är helt gratis.',
  },
  {
    q: 'Vad är en förfrågan?',
    a: 'En förfrågan talar om vad du söker så att säljare kan kontakta dig direkt.',
  },
  {
    q: 'Hur vet jag vilken däckdimension jag behöver?',
    a: 'Dimensionen står på däckets sida (t.ex. 205/55 R16) eller i bilens instruktionsbok.',
  },
  {
    q: 'Vad är skillnaden mellan friktionsdäck och dubbdäck?',
    a: 'Friktionsdäck har mjukare gummiblandning; dubbdäck har metalldubbar för bättre grepp på is.',
  },
  {
    q: 'Hur kontrollerar jag mönsterdjupet?',
    a: 'Använd en mönsterdjupsmätare. Lagkrav är minst 1,6 mm, rekommenderat för vinter är 3+ mm.',
  },
  {
    q: 'Kan jag använda olika fälgar som inte sitter till min bil?',
    a: 'Fälgar måste matcha bultmönster, ET-tal och centrumhål. Dubbelkolla alltid med säljaren.',
  },
  {
    q: 'Vad betyder ET-tal?',
    a: 'ET-talet anger fälgens inpressning i mm — hur långt ut eller in hjulet sitter i förhållande till fälgens mitt.',
  },
  {
    q: 'Vad är kompletta hjul?',
    a: 'Kompletta hjul är däck monterade på fälg — klara att sättas på bilen direkt.',
  },
];

const FAQSection = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className={css.section} id="kontakt">
      <div className={css.container}>
        <div className={css.faqGrid}>
          <div>
            <div className={css.faqIntro}>
              <span className={css.eyebrow}>Support</span>
              <h2>Vanliga frågor</h2>
              <p>Hittar du inte svaret? Kontakta oss så hjälper vi dig.</p>
            </div>
            <div className={css.faqList}>
              {FAQS.map((item, i) => {
                const isOpen = open === i;
                return (
                  <div key={item.q} className={css.faqItem}>
                    <button
                      type="button"
                      className={css.faqTrigger}
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                    >
                      <span>{item.q}</span>
                      <svg
                        className={classNames(css.faqChevron, { [css.faqChevronOpen]: isOpen })}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={classNames(css.faqPanelWrap, { [css.faqPanelWrapOpen]: isOpen })}
                      aria-hidden={!isOpen}
                    >
                      <div className={css.faqPanel}>{item.a}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className={css.faqAside}>
            <div className={css.faqAsideCard}>
              <h3>Behöver du hjälp?</h3>
              <p>Vår oberoende kundsupport guidar dig till rätt hjul.</p>
              <NamedLink
                name="CMSPage"
                params={{ pageId: 'kontakt' }}
                className={css.faqAsideCtaPrimary}
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
                Kontakta oss
              </NamedLink>
              <NamedLink name="SavedSearchesPage" className={css.faqAsideCtaSecondary}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46"
                  />
                </svg>
                Skapa bevakning
              </NamedLink>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const WheelplaceLandingDesktop = () => (
  <div className={css.page}>
    {/* Hero renders immediately (above fold). Everything below fades +
      * rises as it scrolls into view. Reveal triggers once per section. */}
    <Hero />
    <Reveal><FeaturedListings /></Reveal>
    <Reveal><HowItWorks /></Reveal>
    <Reveal><CategoriesSection /></Reveal>
    <Reveal><PlateBand /></Reveal>
    <Reveal><BrandsSection /></Reveal>
    <Reveal><SocialProof /></Reveal>
    <Reveal><ProductEducation /></Reveal>
    <Reveal><Sustainability /></Reveal>
    <Reveal><B2BSection /></Reveal>
    <Reveal><FAQSection /></Reveal>
  </div>
);

/**
 * SSR-safe mobile viewport hook.
 *
 * Lazy-initialises from matchMedia synchronously on the client so the first
 * client render is already the correct tree — no post-paint flash. SSR
 * always gets `false` (desktop), so the server HTML matches the initial
 * client render in that case; on mobile clients React will re-render to
 * the correct value during hydration (one expected hydration mismatch).
 *
 * Subscription uses `useLayoutEffect` so resize-driven swaps happen before
 * the next paint — no brief flicker of the wrong view at the boundary.
 */
/** Mobile tree shown up to 1023px (iPad portrait and all phones). At 1024px+
 *  we switch to the full desktop layout. This gives us a wide buffer away
 *  from scrollbar-induced viewport-width jitter (~15px) that was causing
 *  oscillation at a lower 767px boundary. */
const MOBILE_MQ = '(max-width: 1023px)';

const readIsMobile = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_MQ).matches;
};

// useLayoutEffect is unsafe on the server — fall back to useEffect there.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Returns `{ ready, isMobile }`.
 *
 * `ready` is `false` on the server and during the first client render (before
 * the layout effect has run). Callers should render nothing while not ready —
 * this trades a tiny blank frame for zero flash of the wrong tree.
 */
const useIsMobile = () => {
  const [state, setState] = useState({ ready: false, isMobile: false });

  useIsoLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_MQ);
    const update = () => setState({ ready: true, isMobile: mq.matches });
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  return state;
};

const WheelplaceLanding = () => {
  const { ready, isMobile } = useIsMobile();

  if (!ready) return null;

  return isMobile ? (
    <WheelplaceLandingMobile FeaturedListingsComponent={FeaturedListings} />
  ) : (
    <WheelplaceLandingDesktop />
  );
};

export default WheelplaceLanding;
